export type ErrorReportEntry = {
  message: string;
  path?: string;
  code?: string;
};

export type ErrorReportInput = {
  operationName?: string;
  timestamp: string;
  errors: ErrorReportEntry[];
};

const formatPath = (path?: ReadonlyArray<string | number>): string | undefined =>
  path?.length ? path.join(' > ') : undefined;

const readCode = (value: unknown): string | undefined =>
  typeof value === 'string' && value ? value : undefined;

const formatCode = (extensions?: Record<string, unknown>): string | undefined => {
  if (!extensions) return undefined;
  // The backend (DGS/graphql-java) reports the error kind as `errorType` on a
  // TypedGraphQLError, or as `classification` on a plain graphql-java error —
  // only our own errors carry `code`. Fall back so INTERNAL errors are not
  // reported as an untyped message.
  const top =
    readCode(extensions.code) ??
    readCode(extensions.errorType) ??
    readCode(extensions.classification);
  const inner = (extensions.validationError as { code?: string } | undefined)?.code;
  if (top && inner) return `${top} / ${inner}`;
  return top ?? inner;
};

type GraphQLErrorLike = {
  message: string;
  path?: ReadonlyArray<string | number>;
  extensions?: Record<string, unknown>;
};

export const buildErrorReportFromGraphQLErrors = (
  graphqlErrors: ReadonlyArray<GraphQLErrorLike>,
  options: { operationName?: string; timestamp?: string } = {},
): ErrorReportInput => ({
  operationName: options.operationName,
  timestamp: options.timestamp ?? new Date().toISOString(),
  errors: graphqlErrors.map((error) => ({
    message: error.message,
    path: formatPath(error.path),
    code: formatCode(error.extensions),
  })),
});

type ValidationErrorLike = {
  message?: string | null;
  field?: string | null;
  code?: string | null;
};

const normalizeFieldPath = (field?: string | null): string | undefined => {
  if (!field) return undefined;
  const parts = field.split('/').filter(Boolean);
  return parts.length ? parts.join(' > ') : undefined;
};

export const buildErrorReportFromValidationErrors = (
  validationErrors: ReadonlyArray<ValidationErrorLike>,
  options: { operationName?: string; timestamp?: string; fallbackMessage?: string } = {},
): ErrorReportInput => ({
  operationName: options.operationName,
  timestamp: options.timestamp ?? new Date().toISOString(),
  errors: validationErrors.map((error) => ({
    message: error.message || options.fallbackMessage || error.code || 'Unknown error',
    path: normalizeFieldPath(error.field),
    code: error.code ?? undefined,
  })),
});

export const formatErrorReport = ({
  operationName,
  timestamp,
  errors,
}: ErrorReportInput): string => {
  const lines: string[] = ['--- Error report ---'];
  if (operationName) lines.push(`Operation: ${operationName}`);
  lines.push(`Timestamp: ${timestamp}`);
  errors.forEach((entry, index) => {
    lines.push('');
    lines.push(`Error ${index + 1}: ${entry.message}`);
    if (entry.path) lines.push(`  Path: ${entry.path}`);
    if (entry.code) lines.push(`  Code: ${entry.code}`);
  });
  lines.push('--------------------');
  return lines.join('\n');
};
